// Oscilografias: a lista e o registro com os eventos do SOE alinhados.
//
// Só o que vem do relógio do regulador se alinha com a oscilografia (ver a API). Relógio
// inválido: o registro aparece em tempo relativo e os eventos NÃO são posicionados, com o
// aviso explicando por quê.

import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Crosshair,
  RotateCcw,
  SquareDashedMousePointer,
  Trash2,
  Waves,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { DeleteFilesDialog, FileTrashPage, type TrashKind } from "../components/FileTrash";
import { type OscMarks, OscPanel, type PanelRegistry } from "../components/OscPanel";
import { PostTripButton } from "./analysis";
import { Alert, Badge, Button, Card, EmptyState, PageHeader, Select, Spinner, Table, cx } from "../components/ui";
import { api } from "../lib/api";
import { useTree } from "../lib/hooks";
import { seriesColor } from "../lib/palette";
import { canManageAnything, useMe } from "../lib/session";
import { displayFor, formatDateTimeMs, panelKey } from "../lib/units";

interface OscListRow {
  id: number;
  name: string;
  received_at: string;
  start_ts: string | null;
  start_device_time: string | null;
  samples: number;
  sample_interval_s: number;
  trigger_sample: number | null;
  channels: number;
  trigger_reason: string | null;
  /** Gestor da usina: pode mandar para a lixeira. */
  can_delete: boolean;
  regulator_id: number;
  regulator_name: string;
  unit_name: string;
  site_name: string;
}

interface SoeEvent {
  id: number;
  ts: string;
  device_time: string;
  number: number;
  tag: string;
  description: string;
  state: boolean | null;
}

interface OscDetail {
  id: number;
  name: string;
  receivedAt: string;
  regulator: { id: number; name: string; unit: string; site: string };
  startTs: string | null;
  startDeviceTime: string | null;
  clockValid: boolean;
  samples: number;
  sampleIntervalS: number;
  triggerSample: number | null;
  triggerReason: string | null;
  configMatchesFile: boolean | null;
  channels: { idx: number; addr: number; tag: string; description: string; unit: string; base: string | null; values: number[] }[];
  events: SoeEvent[];
  eventMarginMs: number;
  canDelete: boolean;
}

const fmtDuration = (s: number) => (s >= 1 ? `${s.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} s` : `${Math.round(s * 1000)} ms`);
const fmtRel = (s: number) => `${s.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} s`;

/** Preferência dos cursores A/B neste navegador (ligados por padrão, como nos gráficos). */
const MEASURE_KEY = "insight.osc.cursores";
/** Janela mínima do zoom, em amostras. */
const MIN_SAMPLES = 10;

// ── Lista ────────────────────────────────────────────────────────────────────

export function OscillographyListPage() {
  const { data: tree } = useTree();
  const [params, setParams] = useSearchParams();
  const regulatorId = params.get("reg") ?? "";
  const regs = useMemo(
    () => tree?.groups.flatMap((g) => g.sites.flatMap((s) => s.units.flatMap((u) => u.regulators.map((r) => ({ id: r.id, label: `${s.name} › ${u.name} › ${r.name}` }))))) ?? [],
    [tree],
  );
  const list = useQuery({
    queryKey: ["osc", regulatorId],
    queryFn: () => api.get<{ records: OscListRow[] }>(`/api/oscillography${regulatorId ? `?regulatorId=${regulatorId}` : ""}`),
  });
  const { data: me } = useMe();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const records = list.data?.records ?? [];
  const deletable = records.filter((r) => r.can_delete);
  // Seleção só do que está na tela (trocar o filtro não apaga o que sumiu da lista).
  const chosen = deletable.filter((r) => selected.has(r.id));
  const toggle = (id: number) => setSelected((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set(s).add(id)));
  const allChosen = deletable.length > 0 && chosen.length === deletable.length;

  return (
    <>
      <PageHeader
        title="Oscilografias"
        description="Registros de perturbação enviados pelos reguladores, com a sequência de eventos alinhada."
        actions={
          canManageAnything(me) && (
            <Link
              to="/oscilografias/lixeira"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
            >
              <Trash2 className="size-4" aria-hidden /> Lixeira
            </Link>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select aria-label="Regulador" value={regulatorId} onChange={(e) => setParams(e.target.value ? { reg: e.target.value } : {}, { replace: true })} className="w-auto! min-w-64">
          <option value="">Todos os reguladores</option>
          {regs.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </Select>
        {chosen.length > 0 && (
          <Button variant="danger" onClick={() => setConfirm(true)} className="ml-auto">
            <Trash2 className="size-4" aria-hidden /> Apagar {chosen.length} {chosen.length === 1 ? "registro" : "registros"}
          </Button>
        )}
      </div>
      {confirm && (
        <DeleteDialog
          files={chosen.map((r) => r.name)}
          ids={chosen.map((r) => r.id)}
          onClose={() => setConfirm(false)}
          onDone={() => {
            setConfirm(false);
            setSelected(new Set());
          }}
        />
      )}
      {list.isLoading ? (
        <Spinner />
      ) : !list.data?.records.length ? (
        <EmptyState title="Nenhuma oscilografia recebida" />
      ) : (
        <Table>
          <thead>
            <tr>
              {deletable.length > 0 && (
                <th className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Selecionar todos"
                    checked={allChosen}
                    onChange={() => setSelected(allChosen ? new Set() : new Set(deletable.map((r) => r.id)))}
                    className="accent-[var(--brand)]"
                  />
                </th>
              )}
              <th>Início</th>
              <th>Regulador</th>
              <th>Duração</th>
              <th>Canais</th>
              <th>Disparo</th>
              <th>Arquivo</th>
            </tr>
          </thead>
          <tbody>
            {list.data.records.map((r) => (
              <tr key={r.id} className={cx("hover:bg-surface-2", selected.has(r.id) && "bg-danger/5")}>
                {deletable.length > 0 && (
                  <td>
                    {r.can_delete && (
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${r.name}`}
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                        className="accent-[var(--brand)]"
                      />
                    )}
                  </td>
                )}
                <td className="whitespace-nowrap">
                  <Link to={`/oscilografias/${r.id}`} className="font-medium hover:underline">
                    {r.start_ts ? formatDateTimeMs(r.start_ts) : "sem hora válida"}
                  </Link>
                  {!r.start_ts && <p className="text-xs text-muted">recebido {new Date(r.received_at).toLocaleString("pt-BR")}</p>}
                </td>
                <td className="text-sm text-muted">
                  {r.site_name} › {r.unit_name} › {r.regulator_name}
                </td>
                <td className="tabular-nums">{fmtDuration(r.samples * r.sample_interval_s)}</td>
                <td className="tabular-nums">{r.channels}</td>
                <td>{r.trigger_sample !== null ? <Badge tone="ok">conhecido</Badge> : <Badge>indisponível</Badge>}</td>
                <td>
                  <code className="font-mono text-xs text-muted">{r.name}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

// ── Registro ─────────────────────────────────────────────────────────────────

export function OscillographyPage() {
  const fileId = Number(useParams().id);
  const { data: me } = useMe();
  const { data: osc, isLoading, error } = useQuery({
    queryKey: ["osc-detail", fileId],
    queryFn: () => api.get<OscDetail>(`/api/oscillography/${fileId}`),
  });
  const registry = useRef<PanelRegistry>(new Set()).current;
  const [highlight, setHighlight] = useState<number | null>(null);
  const [cursorX, setCursorX] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const [areaZoom, setAreaZoom] = useState(false);
  // Trecho mostrado (segundos relativos); null = o registro inteiro.
  const [win, setWin] = useState<[number, number] | null>(null);
  const [measure, setMeasure] = useState(() => {
    try {
      return localStorage.getItem(MEASURE_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const [marks, setMarks] = useState<OscMarks>({ a: null, b: null });

  // Tempo relativo: ao disparo, quando conhecido; senão ao início do registro.
  const origin = osc?.triggerSample ?? 0;
  const xs = useMemo(() => (osc ? Array.from({ length: osc.samples }, (_, i) => (i - origin) * osc.sampleIntervalS) : []), [osc, origin]);
  const full = useMemo<[number, number]>(() => (xs.length ? [xs[0]!, xs[xs.length - 1]!] : [0, 1]), [xs]);
  const shown = win ?? full;

  // Outro registro (a rota reaproveita a tela): volta ao inteiro.
  useEffect(() => {
    setWin(null);
    setMarks({ a: null, b: null });
  }, [fileId]);

  // Cursores ligados: começam em 25% e 75% do trecho, e voltam para lá quando o zoom deixa
  // algum deles de fora.
  useEffect(() => {
    // Antes do registro chegar, o trecho é um provisório [0, 1]: nada a posicionar.
    if (!measure || !xs.length) return;
    const [f, t] = shown;
    const inside = (v: number | null) => v !== null && v >= f && v <= t;
    if (!inside(marks.a) || !inside(marks.b)) setMarks({ a: f + (t - f) * 0.25, b: f + (t - f) * 0.75 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, shown[0], shown[1]]);
  const startMs = osc?.startTs ? Date.parse(osc.startTs) : null;
  const originMs = startMs === null || !osc ? null : startMs + origin * osc.sampleIntervalS * 1000;
  const eventX = (e: SoeEvent) => (originMs === null ? null : (Date.parse(e.ts) - originMs) / 1000);
  const markers = useMemo(
    () => (osc?.events ?? []).map((e, i) => ({ x: eventX(e) ?? 0, label: String(i + 1) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [osc, originMs],
  );

  const panels = useMemo(() => {
    const out = new Map<string, { tag: string; description: string; color: string; display: ReturnType<typeof displayFor>; values: number[] }[]>();
    for (const c of osc?.channels ?? []) {
      const d = displayFor(c.base, c.unit, me?.unitPrefs);
      const key = panelKey(c.base, d);
      (out.get(key) ?? out.set(key, []).get(key)!).push({ tag: c.tag, description: c.description, color: seriesColor(c.idx), display: d, values: c.values });
    }
    return [...out.entries()];
  }, [osc, me?.unitPrefs]);

  if (isLoading) return <Spinner />;
  if (error || !osc) return <EmptyState title="Oscilografia não encontrada" />;

  const duration = osc.samples * osc.sampleIntervalS;
  // Evento mais próximo do cursor (até 20 ms), para destacar a linha correspondente.
  const nearest =
    cursorX === null
      ? null
      : osc.events.reduce<number | null>((best, e, i) => {
          const x = eventX(e);
          if (x === null || Math.abs(x - cursorX) > 0.02) return best;
          if (best === null) return i;
          return Math.abs(x - cursorX) < Math.abs((eventX(osc.events[best]!) ?? 0) - cursorX) ? i : best;
        }, null);

  // Zoom: sempre dentro do registro, com um mínimo de amostras.
  const minSpan = Math.min(MIN_SAMPLES * osc.sampleIntervalS, full[1] - full[0]);
  function setWindow(from: number, to: number) {
    if (to < from) [from, to] = [to, from];
    if (to - from < minSpan) {
      const c = (from + to) / 2;
      from = c - minSpan / 2;
      to = c + minSpan / 2;
    }
    const span = Math.min(to - from, full[1] - full[0]);
    from = Math.min(Math.max(from, full[0]), full[1] - span);
    to = from + span;
    setWin(from <= full[0] && to >= full[1] ? null : [from, to]);
  }
  const [from, to] = shown;
  const span = to - from;
  const zoom = (k: number) => {
    const c = (from + to) / 2;
    setWindow(c - (span * k) / 2, c + (span * k) / 2);
  };
  const pan = (k: number) => setWindow(from + span * k, to + span * k);
  const resetWindow = () => setWin(null);
  const dt = marks.a !== null && marks.b !== null ? marks.b - marks.a : null;

  function toggleMeasure() {
    const on = !measure;
    if (!on) setMarks({ a: null, b: null });
    setMeasure(on);
    try {
      localStorage.setItem(MEASURE_KEY, on ? "1" : "0");
    } catch {
      /* navegação privada: só não lembra */
    }
  }

  function goTo(i: number) {
    const x = eventX(osc!.events[i]!);
    setHighlight(i);
    if (x === null) return;
    // Evento fora do trecho ampliado: desloca o trecho para ele, sem mudar o tamanho.
    // O cursor vai depois do quadro seguinte, quando os painéis já estão na escala nova.
    const place = () => {
      for (const u of registry) {
        const left = u.valToPos(x, "x");
        if (Number.isFinite(left)) u.setCursor({ left, top: u.bbox.height / devicePixelRatio / 2 });
      }
    };
    if (x < from || x > to) {
      setWindow(x - span / 2, x + span / 2);
      requestAnimationFrame(place);
    } else place();
  }

  return (
    <>
      <Link to="/oscilografias" className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-fg">
        <ArrowLeft className="size-4" aria-hidden /> Oscilografias
      </Link>
      <PageHeader
        title={osc.startTs ? formatDateTimeMs(osc.startTs) : `Registro ${osc.name}`}
        description={`${osc.regulator.site} › ${osc.regulator.unit} › ${osc.regulator.name}`}
        actions={
          <div className="flex items-start gap-2">
            {osc.canDelete && (
              <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="size-4" aria-hidden /> Apagar
              </Button>
            )}
            <PostTripButton fileId={osc.id} />
          </div>
        }
      />
      {confirmDelete && (
        <DeleteDialog files={[osc.name]} ids={[osc.id]} onClose={() => setConfirmDelete(false)} onDone={() => navigate("/oscilografias")} />
      )}
      <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted">
        <span className="flex items-center gap-1.5">
          <Waves className="size-4" aria-hidden /> {osc.channels.length} canais · {osc.samples} amostras de {osc.sampleIntervalS * 1000} ms · {fmtDuration(duration)}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="size-4" aria-hidden /> relógio do regulador: {osc.startDeviceTime ?? "inválido"}
        </span>
        <code className="font-mono text-xs">{osc.name}</code>
      </div>

      <div className="mb-4 space-y-2">
        {!osc.clockValid && (
          <Alert kind="error">
            O relógio do regulador não tinha hora válida quando este registro foi gravado. O gráfico está em tempo relativo e os eventos do
            SOE <strong>não podem ser alinhados</strong> a ele — acerte o relógio do regulador (ou sincronize por IRIG-B) para os próximos
            registros.
          </Alert>
        )}
        {osc.triggerSample === null && (
          <Alert>
            Instante do disparo indisponível{osc.triggerReason ? `: ${osc.triggerReason}` : ""}. O tempo é contado a partir do início do
            registro.
          </Alert>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-3">
          {/* Zoom e cursores: os mesmos botões da tela de gráficos, valendo para todos os painéis. */}
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Zoom">
            <Button size="sm" variant="secondary" onClick={() => pan(-0.5)} disabled={from <= full[0]} title="Desloca meio trecho para trás">
              <ChevronLeft className="size-4" aria-hidden /> Voltar
            </Button>
            <Button size="sm" variant="secondary" onClick={() => zoom(0.5)} disabled={span <= minSpan} title="Metade do trecho, no mesmo centro">
              <ZoomIn className="size-4" aria-hidden /> Aproximar
            </Button>
            <Button
              size="sm"
              variant={areaZoom ? "primary" : "secondary"}
              onClick={() => setAreaZoom(!areaZoom)}
              aria-pressed={areaZoom}
              title="Com o botão ligado, arraste sobre um gráfico para escolher o trecho a ampliar"
            >
              <SquareDashedMousePointer className="size-4" aria-hidden /> Zoom por área
            </Button>
            <Button size="sm" variant="secondary" onClick={() => zoom(2)} disabled={!win} title="O dobro do trecho, no mesmo centro">
              <ZoomOut className="size-4" aria-hidden /> Afastar
            </Button>
            <Button size="sm" variant="secondary" onClick={() => pan(0.5)} disabled={to >= full[1]} title="Desloca meio trecho para a frente">
              Avançar <ChevronRight className="size-4" aria-hidden />
            </Button>
            <Button size="sm" variant="secondary" onClick={resetWindow} disabled={!win} title="Volta ao registro inteiro">
              <RotateCcw className="size-4" aria-hidden /> Resetar zoom
            </Button>
            <Button
              size="sm"
              variant={measure ? "primary" : "secondary"}
              onClick={toggleMeasure}
              aria-pressed={measure}
              title="Dois cursores para medir a diferença entre dois instantes"
            >
              <Crosshair className="size-4" aria-hidden /> Cursores A/B
            </Button>
            <span className="text-sm text-muted tabular-nums">trecho de {fmtDuration(span)}</span>
          </div>
          {measure && dt !== null && (
            <p className="text-xs text-muted tabular-nums">
              A {fmtRel(marks.a!)} · B {fmtRel(marks.b!)} ·{" "}
              <span className="font-medium text-fg">
                Δt {dt < 0 ? "−" : ""}
                {fmtDuration(Math.abs(dt))}
              </span>
            </p>
          )}
          {panels.map(([key, list]) => (
            <OscPanel
              key={key}
              title={key}
              xs={xs}
              series={list}
              trigger={osc.triggerSample !== null ? 0 : null}
              markers={markers}
              highlight={highlight ?? nearest}
              syncKey={`osc-${osc.id}`}
              registry={registry}
              onCursor={setCursorX}
              xRange={shown}
              areaZoom={areaZoom}
              marks={marks}
              onMark={(k, x) => setMarks((m) => ({ ...m, [k]: Math.min(Math.max(x, from), to) }))}
              onZoom={setWindow}
              onReset={resetWindow}
            />
          ))}
          <p className="text-xs text-muted">
            Tempo em segundos {osc.triggerSample !== null ? "relativo ao disparo" : "desde o início do registro"} · arraste as linhas A e B para
            medir · com “Zoom por área” ligado, arraste num gráfico para ampliar um trecho · duplo clique reseta o zoom.
          </p>
        </div>

        <aside>
          <Card>
            <div className="border-b border-line px-4 py-3">
              <h2 className="font-semibold">Sequência de eventos</h2>
              <p className="text-xs text-muted">
                {osc.clockValid
                  ? `Do registro, com ${osc.eventMarginMs / 1000} s de folga antes e depois.`
                  : "Indisponível: relógio do regulador sem hora válida."}
              </p>
            </div>
            {osc.clockValid && osc.events.length === 0 && <p className="px-4 py-6 text-sm text-muted">Nenhum evento na janela.</p>}
            <ol className="max-h-[70vh] divide-y divide-line overflow-y-auto">
              {osc.events.map((e, i) => {
                const x = eventX(e);
                const outside = x !== null && (x < xs[0]! || x > xs[xs.length - 1]!);
                return (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => goTo(i)}
                      className={cx(
                        "flex w-full gap-3 px-4 py-2.5 text-left text-sm hover:bg-surface-2",
                        (highlight ?? nearest) === i && "bg-brand/10",
                      )}
                    >
                      <span className="mt-0.5 w-5 shrink-0 text-right text-xs font-medium text-muted tabular-nums">{i + 1}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="font-mono text-xs tabular-nums">
                            {x === null ? "—" : `${x >= 0 ? "+" : ""}${(x * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} ms`}
                          </span>
                          {e.state === null ? null : e.state ? (
                            <Badge tone="warn">ativou</Badge>
                          ) : (
                            <Badge>normalizou</Badge>
                          )}
                        </span>
                        <span className="block">{e.description}</span>
                        <span className="block text-xs text-muted">
                          {e.device_time} {outside && "· fora do registro (folga)"}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </Card>
          {osc.clockValid && osc.events.length > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-muted">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
              Alarmes e estados vistos pela nuvem não entram aqui: são marcados pelo relógio do gateway, que não é o do regulador.
            </p>
          )}
        </aside>
      </div>
    </>
  );
}

// ── Apagar e lixeira ─────────────────────────────────────────────────────────

const OSC_TRASH: TrashKind = {
  api: "/api/oscillography",
  invalidate: [["osc"], ["osc-detail"]],
  one: "oscilografia",
  many: "oscilografias",
  listPath: "/oscilografias",
  listLabel: "Oscilografias",
};

function DeleteDialog(props: { ids: number[]; files: string[]; onClose: () => void; onDone: () => void }) {
  return <DeleteFilesDialog kind={OSC_TRASH} {...props} />;
}

export function OscillographyTrashPage() {
  return <FileTrashPage kind={OSC_TRASH} title="Lixeira de oscilografias" />;
}

